import { createFileRoute, Link, notFound, useNavigate } from '@tanstack/react-router';
import { useCallback, useEffect, useRef, useState } from 'react';
import type * as React from 'react';
import mapboxgl from 'mapbox-gl';
import { useAuth } from '../lib/auth/auth-context';
import { supabase as defaultClient } from '../lib/supabase';
import { captureSupabaseError } from '../lib/sentry-supabase';
import type { LynrxSupabaseClient } from '@lynrx/shared';
import { Alert } from '../components/ui/Alert';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import {
  RevealablePhiCell,
  type RevealField,
  type RevealFn,
} from '../components/RevealablePhiCell';
import { useSetPageHeader } from '../components/layout/PageHeaderContext';

// Patient detail page. Mirrors the list (#69) discipline: the
// browser-client read selects ONLY plaintext columns — `full_name`,
// `phone`, `latitude`, `longitude` ciphertext never reaches the
// browser. Name + phone are surfaced through <RevealablePhiCell>,
// which hits POST /api/patients/reveal for server-side decrypt + a
// per-field `log_phi_access` audit row. One click → one audit row.
//
// No bulk reveal endpoint, no decryptPHIBatch — the audit story stays
// honest about what was actually viewed. Coordinates are intentionally
// not surfaced: they're machine values for geofence math, not a
// dispatcher-facing artifact.
//
// The page header title uses `external_patient_id` (or a generic
// "Patient" fallback) — never the decrypted name. PHI must not leak
// into the top-bar / browser-tab title.

const PATIENT_ID_UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function isPatientIdParam(value: unknown): value is string {
  return typeof value === 'string' && PATIENT_ID_UUID_RE.test(value);
}

// No full_name / phone — those are reveal-only. latitude/longitude
// are safe to include as plaintext numerics since 2026-08-18 (the
// bytea → numeric revert + safe-harbor rationale — coordinates alone
// are not PHI). Used only to render the small locator map on the
// Info tab; never displayed as digits.
const PATIENT_DETAIL_COLUMNS =
  'id, external_patient_id, email, date_of_birth, address_line_1, address_line_2, city, state, zip, latitude, longitude, delivery_notes, sms_consent, sms_opted_out_at, created_at';

export type PatientDetailRow = {
  id: string;
  external_patient_id: string | null;
  email: string | null;
  date_of_birth: string | null;
  address_line_1: string | null;
  address_line_2: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  latitude: string | number | null;
  longitude: string | number | null;
  delivery_notes: string | null;
  sms_consent: boolean;
  sms_opted_out_at: string | null;
  created_at: string;
};

// Optional `q` so back-navigation can preserve the list's search term
// without forcing every link into /patients/$id to carry one.
export const Route = createFileRoute('/_authenticated/patients/$patientId')({
  component: PatientDetailPage,
  notFoundComponent: PatientNotFoundRoute,
  parseParams: (params): { patientId: string } => {
    if (!isPatientIdParam(params.patientId)) {
      throw notFound();
    }
    return { patientId: params.patientId };
  },
  validateSearch: (search: Record<string, unknown>): { q?: string } => {
    const raw = search['q'];
    if (typeof raw === 'string' && raw.trim().length > 0) return { q: raw };
    return {};
  },
});

type DetailState =
  | { kind: 'loading' }
  | { kind: 'loaded'; patient: PatientDetailRow }
  | { kind: 'not_found' }
  | { kind: 'error'; message: string };

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  // YYYY-MM-DD literal (date_of_birth is a `date` column) — parse as
  // local calendar date so negative-UTC offsets don't render the
  // previous day.
  if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) {
    const [y, m, d] = iso.split('-').map(Number) as [number, number, number];
    return new Date(y, m - 1, d).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }
  // ISO timestamp (created_at, sms_opted_out_at) — let Date parse it.
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return iso;
  return dt.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function addressDisplay(row: PatientDetailRow): string {
  const line1 = [row.address_line_1, row.address_line_2]
    .filter((p) => p && p.trim().length > 0)
    .join(' ');
  const cityState = [row.city, row.state].filter(Boolean).join(', ');
  const parts = [line1, cityState, row.zip ?? ''].filter(
    (p) => p && p.trim().length > 0,
  );
  return parts.length > 0 ? parts.join(' · ') : '—';
}

function PatientDetailPage(): JSX.Element {
  const { patientId } = Route.useParams();
  const { q } = Route.useSearch();
  const navigate = useNavigate();
  const onBack = useCallback(() => {
    void navigate({ to: '/patients', search: q ? { q } : {} });
  }, [navigate, q]);
  return <PatientDetailView patientId={patientId} backQuery={q} onBack={onBack} />;
}

type ViewProps = {
  patientId: string;
  // Required-but-nullable so callers can forward an undefined search
  // term without the `exactOptionalPropertyTypes` compiler tripping.
  backQuery: string | undefined;
  onBack: () => void;
  // Test seam — defaults to the singleton supabase client.
  client?: LynrxSupabaseClient;
  // Test seam — defaults to the real /api/patients/reveal fetch.
  reveal?: RevealFn;
};

export function PatientDetailView({
  patientId,
  backQuery,
  onBack,
  client = defaultClient,
  reveal,
}: ViewProps): JSX.Element {
  const { session, isReady } = useAuth();
  const [state, setState] = useState<DetailState>({ kind: 'loading' });

  // Header title MUST NOT leak decrypted PHI. External patient id (the
  // pharmacy's own identifier) or a generic label — never the name.
  const headerTitle =
    state.kind === 'loaded' && state.patient.external_patient_id
      ? state.patient.external_patient_id
      : 'Patient';
  useSetPageHeader({
    title: headerTitle,
    parentLabel: 'Patients',
    parentTo: '/patients',
  });

  useEffect(() => {
    if (!isReady || !session) return;
    let cancelled = false;
    setState({ kind: 'loading' });

    void (async () => {
      // RLS scopes the read to the caller's tenant/pharmacy. The
      // column list excludes every encrypted bytea column on patients
      // (`full_name`, `phone`, `latitude`, `longitude`) — those are
      // reached only through the reveal endpoint.
      const { data, error } = await client
        .from('patients')
        .select(PATIENT_DETAIL_COLUMNS)
        .eq('id', patientId)
        .is('deleted_at', null)
        .maybeSingle();

      if (cancelled) return;

      if (error) {
        captureSupabaseError(error, 'patients.detail.fetch');
        setState({ kind: 'error', message: 'Could not load this patient.' });
        return;
      }
      if (!data) {
        setState({ kind: 'not_found' });
        return;
      }
      setState({
        kind: 'loaded',
        patient: data as unknown as PatientDetailRow,
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [client, session, isReady, patientId]);

  // Default reveal: POST the caller's token to the server-side route,
  // which decrypts + logs access and returns the single plaintext
  // value. Identical wire shape to the list's defaultReveal.
  const defaultReveal: RevealFn = useCallback(
    async (id: string, field: RevealField) => {
      if (!session) throw new Error('no_session');
      const res = await fetch('/api/patients/reveal', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ patient_id: id, field }),
      });
      if (!res.ok) {
        throw new Error(`reveal_failed_${res.status}`);
      }
      const data = (await res.json()) as { value: string | null };
      return data.value;
    },
    [session],
  );
  const revealFn = reveal ?? defaultReveal;

  return (
    <div className="mx-auto w-full">
      {/* No inline "Back to patients" link — the shell breadcrumb
          (Patients / Patient) at the top-left already offers the
          navigation. Two ways to go back was noise. */}

      {state.kind === 'loading' ? (
        <Card>
          <p className="text-sm text-ink-2">Loading patient…</p>
        </Card>
      ) : null}

      {state.kind === 'not_found' ? <PatientNotFoundCard onBack={onBack} /> : null}

      {state.kind === 'error' ? (
        <Alert tone="error" title="Something went wrong">
          {state.message}
        </Alert>
      ) : null}

      {state.kind === 'loaded' ? (
        <LoadedView patient={state.patient} reveal={revealFn} client={client} />
      ) : null}
    </div>
  );
}

type TabKey = 'info' | 'deliveries' | 'followups' | 'access';

const TABS: Array<{ key: TabKey; label: string }> = [
  { key: 'info', label: 'Info' },
  { key: 'deliveries', label: 'Deliveries' },
  { key: 'followups', label: 'Follow-ups' },
  { key: 'access', label: 'Access log' },
];

function LoadedView({
  patient,
  reveal,
  client,
}: {
  patient: PatientDetailRow;
  reveal: RevealFn;
  client: LynrxSupabaseClient;
}): JSX.Element {
  // Local tab state — not URL-synced (Phase 2 nicety; deep-linking to
  // a specific tab isn't a real user request yet). Default 'info' so
  // the page still opens on the identity card, matching prior UX.
  const [tab, setTab] = useState<TabKey>('info');
  return (
    <>
      {/* Compact header row: tab strip on the left, tiny "added"
          date on the right. Dropped the standalone card that just
          said "Patient · Added <date>" — the breadcrumb up-top
          already labels the page, and the ext ID (if any) shows in
          the Identity & contact section. */}
      <div className="mb-4 flex items-center justify-between gap-3">
        <div className="inline-flex rounded-lg border border-border bg-bg p-1 shadow-card">
          {TABS.map((t) => {
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setTab(t.key)}
                className={
                  'rounded-md px-3 py-1.5 text-sm font-medium transition ' +
                  (active
                    ? 'bg-ink text-white'
                    : 'text-ink-2 hover:bg-bg-hover hover:text-ink')
                }
              >
                {t.label}
              </button>
            );
          })}
        </div>
        <span className="text-xs text-ink-3">
          Added {formatDate(patient.created_at)}
        </span>
      </div>

      {tab === 'info' && (
        <InfoTab patient={patient} reveal={reveal} client={client} />
      )}
      {tab === 'deliveries' && (
        <DeliveriesTab patientId={patient.id} client={client} />
      )}
      {tab === 'followups' && (
        <FollowUpsTab patientId={patient.id} client={client} />
      )}
      {tab === 'access' && (
        <AccessLogTab patientId={patient.id} client={client} />
      )}
    </>
  );
}

// Existing patient info — same content as before, extracted into a
// tab component so LoadedView can swap panels. Prefixed with a
// SummaryCard (delivery counts) and the Address section now embeds
// a small locator map under the address text.
function InfoTab({
  patient,
  reveal,
  client,
}: {
  patient: PatientDetailRow;
  reveal: RevealFn;
  client: LynrxSupabaseClient;
}): JSX.Element {
  return (
    <div className="grid gap-3">
      <SummaryCard patientId={patient.id} client={client} />

      <section>
        <h2 className="mb-1.5 text-sm font-medium text-ink">Identity &amp; contact</h2>
        <Card>
          <dl className="grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
            <DlItem
              label="Name"
              value={
                <RevealablePhiCell
                  patientId={patient.id}
                  field="full_name"
                  label="name"
                  reveal={reveal}
                />
              }
            />
            <DlItem
              label="Phone"
              value={
                <RevealablePhiCell
                  patientId={patient.id}
                  field="phone"
                  label="phone"
                  reveal={reveal}
                />
              }
            />
            <DlItem label="Email" value={patient.email ?? '—'} />
            <DlItem
              label="Date of birth"
              value={formatDate(patient.date_of_birth)}
            />
            <DlItem
              label="Patient ID"
              value={patient.external_patient_id ?? '—'}
            />
          </dl>
        </Card>
      </section>

      <section>
        <h2 className="mb-1.5 text-sm font-medium text-ink">Address</h2>
        <Card className="overflow-hidden !p-0">
          <MiniLocationMap patient={patient} />
          <div className="px-3 py-2">
            <p className="text-sm text-ink">{addressDisplay(patient)}</p>
          </div>
        </Card>
      </section>

      <section>
        <h2 className="mb-1.5 text-sm font-medium text-ink">Preferences</h2>
        <Card>
          <dl className="grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
            <DlItem
              label="SMS consent"
              value={
                patient.sms_opted_out_at
                  ? `Opted out ${formatDateTime(patient.sms_opted_out_at)}`
                  : patient.sms_consent
                    ? 'Consented'
                    : '—'
              }
            />
            <DlItem
              label="Delivery notes"
              value={
                patient.delivery_notes && patient.delivery_notes.length > 0
                  ? patient.delivery_notes
                  : '—'
              }
            />
          </dl>
        </Card>
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------
// SummaryCard — small stats strip at the top of the Info tab.
// Answers "who is this patient at a glance" before the user picks
// a tab: how many deliveries have they had, when was the last one.
// One lightweight query; count is the exact PostgREST count header,
// last-delivered is the single most-recent row.

function SummaryCard({
  patientId,
  client,
}: {
  patientId: string;
  client: LynrxSupabaseClient;
}): JSX.Element {
  const [totalDeliveries, setTotalDeliveries] = useState<number | null>(null);
  const [lastDelivery, setLastDelivery] = useState<string | null>(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error: err, count } = await client
        .from('orders')
        .select('id, deliveries(delivered_at)', { count: 'exact' })
        .eq('patient_id', patientId)
        .is('deleted_at', null)
        .order('created_at', { ascending: false })
        .limit(1);
      if (cancelled) return;
      if (err) {
        captureSupabaseError(err, 'patient.summary.fetch');
        setError(true);
        return;
      }
      setTotalDeliveries(count ?? 0);
      const latest =
        (data?.[0] as unknown as {
          deliveries?: Array<{ delivered_at: string | null }>;
        } | undefined)?.deliveries?.[0]?.delivered_at ?? null;
      setLastDelivery(latest);
    })();
    return () => {
      cancelled = true;
    };
  }, [patientId, client]);

  if (error) return <></>; // Silent failure — summary is nice-to-have.
  return (
    <Card className="!py-2 !px-3">
      <dl className="flex flex-wrap gap-x-6 gap-y-1 text-sm">
        <div className="flex items-baseline gap-2">
          <dt className="text-[10px] font-medium uppercase tracking-wider text-ink-3">
            Deliveries
          </dt>
          <dd className="text-sm font-semibold text-ink">
            {totalDeliveries ?? '—'}
          </dd>
        </div>
        <div className="flex items-baseline gap-2">
          <dt className="text-[10px] font-medium uppercase tracking-wider text-ink-3">
            Last
          </dt>
          <dd className="text-sm font-semibold text-ink">
            {lastDelivery ? formatDate(lastDelivery) : '—'}
          </dd>
        </div>
      </dl>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// MiniLocationMap — small Mapbox pin at the patient's geocoded
// address. Uses the plaintext-numeric lat/lng from the 2026-08-18
// bytea → numeric revert. Falls back to nothing rendered if coords
// are missing (patient wasn't in the backfill or hasn't been
// geocoded yet). Same style + red pin as the /patients Map view for
// visual consistency across surfaces.

function MiniLocationMap({ patient }: { patient: PatientDetailRow }): JSX.Element {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);

  const lat =
    typeof patient.latitude === 'string'
      ? Number(patient.latitude)
      : patient.latitude;
  const lng =
    typeof patient.longitude === 'string'
      ? Number(patient.longitude)
      : patient.longitude;
  const hasCoords =
    lat !== null &&
    lng !== null &&
    !Number.isNaN(lat as number) &&
    !Number.isNaN(lng as number);

  useEffect(() => {
    if (!hasCoords) return;
    const container = containerRef.current;
    if (!container) return;
    const token = import.meta.env.VITE_MAPBOX_ACCESS_TOKEN;
    if (!token) return; // Silent — the outer Card just shows address.
    mapboxgl.accessToken = token;
    const map = new mapboxgl.Map({
      container,
      style: 'mapbox://styles/mapbox/streets-v12',
      center: [lng as number, lat as number],
      zoom: 15, // Street level — the map is 200px tall, no space to zoom out.
      interactive: false, // No pan/zoom — this is a locator, not an explorer.
    });
    mapRef.current = map;
    // Classic red teardrop marker, inline so this locator map has no
    // component-level dependencies. Small enough to be a data URI.
    const el = document.createElement('div');
    el.style.cssText = 'width:22px;height:30px;filter:drop-shadow(0 2px 3px rgba(0,0,0,0.35));';
    el.innerHTML = `
      <svg width="22" height="30" viewBox="0 0 22 30" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <path d="M11 0C4.925 0 0 4.925 0 11c0 8.25 11 19 11 19s11-10.75 11-19C22 4.925 17.075 0 11 0z" fill="#dc2626" stroke="rgba(255,255,255,0.98)" stroke-width="1.5" />
        <circle cx="11" cy="11" r="4" fill="rgba(255,255,255,0.98)" />
      </svg>
    `;
    new mapboxgl.Marker({ element: el, anchor: 'bottom' })
      .setLngLat([lng as number, lat as number])
      .addTo(map);
    return () => {
      map.remove();
      mapRef.current = null;
    };
  }, [hasCoords, lat, lng]);

  if (!hasCoords) return <></>;
  return <div ref={containerRef} className="h-48 w-full" data-testid="patient-mini-map" />;
}

// ---------------------------------------------------------------------------
// Deliveries tab — every order this patient has been the dropoff for,
// most-recent first. Query goes through the token-bound client so RLS
// scopes to the caller's tenant/pharmacy_access automatically.

type DeliveryRow = {
  id: string;
  order_number: string | null;
  status: string;
  created_at: string;
  patient_id: string | null;
  dropoff_stop: {
    patient_id: string | null;
    route: {
      id: string;
      planned_for: string;
      driver: { full_name: string | null; email: string } | null;
    } | null;
  } | null;
  deliveries: Array<{ delivered_at: string | null }> | null;
  pod_records: Array<{ recipient_name: string | null }> | null;
};

function DeliveriesTab({
  patientId,
  client,
}: {
  patientId: string;
  client: LynrxSupabaseClient;
}): JSX.Element {
  const [rows, setRows] = useState<DeliveryRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Filter via orders.patient_id (the direct FK added in PR #61)
      // rather than through the stops relation — cleaner PostgREST
      // syntax and less surface for the "!inner join" gotcha. Signed-
      // by comes from pod_records.recipient_name; deliveries only has
      // the timestamp + geofence data. Signed-by comes from
      // pod_records.recipient_name; deliveries carries the timestamp
      // + geofence data.
      const { data, error: err } = await client
        .from('orders')
        .select(
          'id, order_number, status, created_at, patient_id, ' +
            'dropoff_stop:stops!orders_dropoff_stop_id_fkey(' +
            'patient_id, ' +
            'route:routes!stops_route_id_fkey(id, planned_for, ' +
            'driver:users!routes_driver_id_fkey(full_name, email))), ' +
            'deliveries(delivered_at), ' +
            'pod_records(recipient_name)',
        )
        .eq('patient_id', patientId)
        .is('deleted_at', null)
        .order('created_at', { ascending: false })
        .limit(500);
      if (cancelled) return;
      if (err) {
        captureSupabaseError(err, 'patient.deliveries.fetch');
        setError('Could not load delivery history.');
        return;
      }
      setRows((data ?? []) as unknown as DeliveryRow[]);
    })();
    return () => {
      cancelled = true;
    };
  }, [patientId, client]);

  if (error) return <Alert tone="error">{error}</Alert>;
  if (rows === null) {
    return (
      <Card className="p-4 text-sm text-ink-3">Loading deliveries…</Card>
    );
  }
  // With .eq('patient_id', patientId) on the top-level orders row,
  // no post-filter needed; keep the variable name for parity with
  // the follow-ups / access-log tabs below.
  const scoped = rows;
  if (scoped.length === 0) {
    return (
      <Card className="p-6 text-sm text-ink-2">
        No deliveries on record for this patient.
      </Card>
    );
  }
  return (
    <Card className="overflow-hidden p-0">
      <table className="w-full text-sm">
        <thead className="border-b border-border bg-bg-soft text-left text-xs uppercase tracking-wide text-ink-3">
          <tr>
            <th className="px-4 py-2 font-medium">Date</th>
            <th className="px-4 py-2 font-medium">Order #</th>
            <th className="px-4 py-2 font-medium">Driver</th>
            <th className="px-4 py-2 font-medium">Status</th>
            <th className="px-4 py-2 font-medium">Signed by</th>
            <th className="px-4 py-2 font-medium text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {scoped.map((r) => {
            const delivered = r.deliveries?.[0]?.delivered_at ?? null;
            const date = delivered ?? r.created_at;
            const driver =
              r.dropoff_stop?.route?.driver?.full_name?.split(' ')[0] ??
              r.dropoff_stop?.route?.driver?.email ??
              '—';
            const signedBy = r.pod_records?.[0]?.recipient_name ?? '—';
            return (
              <tr key={r.id} className="border-b border-border/60 last:border-0">
                <td className="px-4 py-2 text-ink">{formatDate(date)}</td>
                <td className="px-4 py-2 font-mono text-xs text-ink-2">
                  {r.order_number ?? r.id.slice(0, 8)}
                </td>
                <td className="px-4 py-2 text-ink">{driver}</td>
                <td className="px-4 py-2">
                  <StatusPill status={r.status} />
                </td>
                <td className="px-4 py-2 text-ink-2">{signedBy}</td>
                <td className="px-4 py-2 text-right">
                  <Link
                    to="/orders/$orderId"
                    params={{ orderId: r.id }}
                    search={{ status: 'all' }}
                    className="text-xs font-medium text-primary hover:underline"
                  >
                    View POD →
                  </Link>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </Card>
  );
}

// Simple status pill — reuses the tone vocabulary from OrdersList
// without importing that page's StatusBadge (which pulls order-list
// concerns). Kept minimal on purpose.
function StatusPill({ status }: { status: string }): JSX.Element {
  const tone =
    status === 'delivered'
      ? 'bg-green-100 text-green-800'
      : status === 'failed' || status === 'returned' || status === 'cancelled'
        ? 'bg-red-100 text-red-800'
        : status === 'attempted'
          ? 'bg-amber-100 text-amber-800'
          : 'bg-slate-100 text-slate-700';
  return (
    <span
      className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium capitalize ${tone}`}
    >
      {status.replace(/_/g, ' ')}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Follow-ups tab — order_exceptions for orders belonging to this
// patient. Two-step query: first fetch this patient's order ids
// (small result), then fetch exceptions IN those ids. Simpler than
// PostgREST's `!inner` join semantics for filtered-nested queries.

type ExceptionRow = {
  id: string;
  order_id: string;
  exception_type: string;
  decision: string | null;
  decided_by: string | null;
  decided_at: string | null;
  reason_code: string | null;
  notes: string | null;
  created_at: string;
};

function FollowUpsTab({
  patientId,
  client,
}: {
  patientId: string;
  client: LynrxSupabaseClient;
}): JSX.Element {
  const [rows, setRows] = useState<ExceptionRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // Two-step: fetch this patient's order ids (via the direct
      // orders.patient_id FK from PR #61), then exceptions IN that
      // set. Cleaner than a nested join across order_exceptions →
      // orders → stops.
      const { data: orderRows, error: orderErr } = await client
        .from('orders')
        .select('id')
        .eq('patient_id', patientId)
        .is('deleted_at', null);
      if (cancelled) return;
      if (orderErr) {
        captureSupabaseError(orderErr, 'patient.followups.orders_fetch');
        setError('Could not load follow-up history.');
        return;
      }
      const orderIds = (orderRows ?? []).map(
        (r) => (r as unknown as { id: string }).id,
      );
      if (orderIds.length === 0) {
        setRows([]);
        return;
      }
      const { data, error: err } = await client
        .from('order_exceptions')
        .select(
          'id, order_id, exception_type, decision, decided_by, decided_at, reason_code, notes, created_at',
        )
        .in('order_id', orderIds)
        .order('created_at', { ascending: false });
      if (cancelled) return;
      if (err) {
        captureSupabaseError(err, 'patient.followups.fetch');
        setError('Could not load follow-up history.');
        return;
      }
      setRows((data ?? []) as unknown as ExceptionRow[]);
    })();
    return () => {
      cancelled = true;
    };
  }, [patientId, client]);

  if (error) return <Alert tone="error">{error}</Alert>;
  if (rows === null) {
    return (
      <Card className="p-4 text-sm text-ink-3">Loading follow-ups…</Card>
    );
  }
  if (rows.length === 0) {
    return (
      <Card className="p-6 text-sm text-ink-2">
        This patient has no follow-up history.
      </Card>
    );
  }
  return (
    <Card className="overflow-hidden p-0">
      <table className="w-full text-sm">
        <thead className="border-b border-border bg-bg-soft text-left text-xs uppercase tracking-wide text-ink-3">
          <tr>
            <th className="px-4 py-2 font-medium">Raised</th>
            <th className="px-4 py-2 font-medium">Type</th>
            <th className="px-4 py-2 font-medium">Decision</th>
            <th className="px-4 py-2 font-medium">Reason</th>
            <th className="px-4 py-2 font-medium">Decided</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} className="border-b border-border/60 last:border-0">
              <td className="px-4 py-2 text-ink">
                {formatDateTime(r.created_at)}
              </td>
              <td className="px-4 py-2 capitalize text-ink">
                {r.exception_type.replace(/_/g, ' ')}
              </td>
              <td className="px-4 py-2 capitalize text-ink">
                {r.decision ? r.decision.replace(/_/g, ' ') : '—'}
              </td>
              <td className="px-4 py-2 text-ink-2">
                {r.reason_code ? r.reason_code.replace(/_/g, ' ') : '—'}
              </td>
              <td className="px-4 py-2 text-ink-2">
                {r.decided_at ? formatDateTime(r.decided_at) : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Access log tab — audit_events for patient.phi_accessed on this
// patient. Compliance staple: shows who revealed name/phone and when.

type AuditRow = {
  id: string;
  occurred_at: string;
  event_type: string;
  actor_user_id: string | null;
  payload: Record<string, unknown> | null;
};

function AccessLogTab({
  patientId,
  client,
}: {
  patientId: string;
  client: LynrxSupabaseClient;
}): JSX.Element {
  const [rows, setRows] = useState<AuditRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // /api/patients/reveal calls the log_phi_access RPC with
      // p_entity_type='patient' + p_entity_id=<patient_uuid>, which
      // writes an audit_events row with event_type =
      // 'patient.phi_accessed' AND entity_type='patient' AND
      // entity_id=<patient_uuid>. Filter on the top-level columns
      // (not payload) — those are what log_phi_access populates.
      const { data, error: err } = await client
        .from('audit_events')
        .select('id, occurred_at, event_type, actor_user_id, payload')
        .eq('event_type', 'patient.phi_accessed')
        .eq('entity_type', 'patient')
        .eq('entity_id', patientId)
        .order('occurred_at', { ascending: false })
        .limit(500);
      if (cancelled) return;
      if (err) {
        captureSupabaseError(err, 'patient.access_log.fetch');
        setError('Could not load access log.');
        return;
      }
      setRows((data ?? []) as unknown as AuditRow[]);
    })();
    return () => {
      cancelled = true;
    };
  }, [patientId, client]);

  if (error) return <Alert tone="error">{error}</Alert>;
  if (rows === null) {
    return (
      <Card className="p-4 text-sm text-ink-3">Loading access log…</Card>
    );
  }
  if (rows.length === 0) {
    return (
      <Card className="p-6 text-sm text-ink-2">
        No PHI access recorded for this patient yet.
      </Card>
    );
  }
  return (
    <Card className="overflow-hidden p-0">
      <table className="w-full text-sm">
        <thead className="border-b border-border bg-bg-soft text-left text-xs uppercase tracking-wide text-ink-3">
          <tr>
            <th className="px-4 py-2 font-medium">Timestamp</th>
            <th className="px-4 py-2 font-medium">Field revealed</th>
            <th className="px-4 py-2 font-medium">Actor</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const field =
              (r.payload?.['field'] as string | undefined) ?? 'unknown';
            const actor = r.actor_user_id ?? '—';
            return (
              <tr key={r.id} className="border-b border-border/60 last:border-0">
                <td className="px-4 py-2 text-ink">
                  {formatDateTime(r.occurred_at)}
                </td>
                <td className="px-4 py-2 capitalize text-ink">
                  {field.replace(/_/g, ' ')}
                </td>
                <td className="px-4 py-2 font-mono text-xs text-ink-2">
                  {actor.slice(0, 8)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </Card>
  );
}

function DlItem({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}): JSX.Element {
  return (
    <div>
      <dt className="text-xs text-ink-2">{label}</dt>
      <dd className="text-ink">{value}</dd>
    </div>
  );
}

export function PatientNotFoundCard({
  onBack,
}: {
  onBack: () => void;
}): JSX.Element {
  return (
    <Alert tone="error" title="Patient not found">
      We couldn&apos;t find a patient with that ID.
      <div className="mt-3">
        <Button variant="secondary" onClick={onBack}>
          Back to patients
        </Button>
      </div>
    </Alert>
  );
}

function PatientNotFoundRoute(): JSX.Element {
  const navigate = useNavigate();
  const onBack = useCallback(() => {
    void navigate({ to: '/patients', search: {} });
  }, [navigate]);
  return (
    <main className="min-h-screen bg-bg-soft px-6 py-12">
      <div className="mx-auto w-full">
        <div className="mb-6">
          <Link
            to="/patients"
            search={{}}
            className="text-sm text-primary hover:underline"
            onClick={(e: React.MouseEvent) => {
              e.preventDefault();
              onBack();
            }}
          >
            ← Back to patients
          </Link>
        </div>
        <PatientNotFoundCard onBack={onBack} />
      </div>
    </main>
  );
}
